import React from 'react';
import { usePresentationContext } from '../../../../legacy-context/presentationContext';
import type { VideoState } from '../../utils';
import { AutoplayNotification } from '../AutoplayNotification';

function until(conditionFunction: () => boolean) {
  const poll = (resolve: (_?: unknown) => void) => {
    if (conditionFunction()) resolve();
    else setTimeout(() => poll(resolve), 100);
  };
  return new Promise(poll);
}

class AsyncYoutubePlayer {
  player: any;
  constructor(player: any) {
    this.player = player;
  }

  waitUntilPlayerStateIs(value: number | number[]) {
    const check = !Array.isArray(value) ? [value] : value;
    return until(() => {
      return check.includes(this.player.getPlayerState());
    });
  }

  pause = () => {
    this.player.pauseVideo();
    return this.waitUntilPlayerStateIs([2, 5]);
  };

  play = () => {
    this.player.playVideo();
    return this.waitUntilPlayerStateIs(1);
  };
}

interface YoutubePlayerProps {
  videoUrl: string;
  shouldRun: boolean;
}

export const YoutubePlayer = ({ videoUrl, shouldRun }: YoutubePlayerProps) => {
  const { useSlideState } = usePresentationContext();
  const [{ videoState }, setSlideState] = useSlideState<VideoState>();
  const player = React.useRef<any>(null);
  const [playerReady, setPlayerReady] = React.useState(false);
  const [isAutoPlayBlocked, setIsAutoPlayBlocked] = React.useState(false);
  const promise = React.useRef<Promise<unknown> | null>(null);

  React.useEffect(() => {
    if (shouldRun && !player.current) {
      const youtubePlayer = new window.YT.Player('video-slide-player', {
        events: {
          onReady: () => {
            setPlayerReady(true);
            //When modal opens video should start playing
            setTimeout(() => {
              setSlideState({
                videoState: 'playing',
              });
            }, 1000);
          },
          onAutoplayBlocked: () => {
            // Some browsers block autoplay, so we need to reset button state
            setIsAutoPlayBlocked(true);
            setSlideState({
              videoState: 'paused',
            });
          },
          onStateChange: (event: { data: any }) => {
            switch (event.data) {
              case window.YT.PlayerState.PLAYING:
                setIsAutoPlayBlocked(false);
                setPlayerReady(true);
                setSlideState({
                  videoState: 'playing',
                });
                break;
              case window.YT.PlayerState.PAUSED:
                setSlideState({
                  videoState: 'paused',
                });
                break;
              default:
                break;
            }
          },
        },
      });
      player.current = youtubePlayer;
    }
  }, [setSlideState, shouldRun]);

  React.useEffect(() => {
    const handlePlaying = async () => {
      const asyncPlayer = new AsyncYoutubePlayer(player.current);
      if (playerReady && player.current?.playVideo && !isAutoPlayBlocked) {
        // wait if youtube player is doing an update
        await promise.current;
        const playerState = player.current.getPlayerState();
        if (videoState === 'playing') {
          if (playerState !== 1) {
            promise.current = asyncPlayer.play();
          }
        } else {
          if (![2, 5].includes(playerState)) {
            promise.current = asyncPlayer.pause();
          }
        }
      }
    };
    handlePlaying();
  }, [isAutoPlayBlocked, playerReady, videoState]);

  return (
    <>
      {isAutoPlayBlocked && <AutoplayNotification />}
      <iframe
        id="video-slide-player"
        src={videoUrl}
        sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
        height="75%"
        width="75%"
        allowFullScreen
        allow="autoplay"
      />
    </>
  );
};
